import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';

// Piece 4c (#2151) — the founder review surface for suggestions Piece 4a
// captures and Piece 4b's PATCH route accepts/rejects. This page does NOT
// mutate the PRD itself: Accept/Reject call the existing
// PATCH /refinement-suggestions/:id route, and the server does the edit +
// regenerate (4b). One id per action, always — no select-all, no
// accept-by-filter (recon RQ7's per-item gate).
//
// CLIENT_SPONSOR exclusion is structural, not a client-side role check
// (this app has none — see AuthContext's User type, {id,email,name} only):
// this page lives under /projects/:projectId/*, a route tree CLIENT_SPONSOR
// never navigates into (their surfaces are all under /client/*), and the
// backend GET/PATCH routes both gate on assertArtefactTenantWriteAccess,
// which 403s a project_members-only caller regardless of what the UI does.
//
// #2364 — "Generate suggestions" below calls the existing
// POST /refinement-suggestions/capture route (added in #2285/Piece 4a,
// never wired to any caller until now — the recon that filed #2364 traced
// this as the ONLY missing hop in the whole guided-answer -> PRD chain;
// everything downstream, including this page's own Accept/Reject, was
// already working). That route deliberately uses the LOOSER
// assertArtefactProjectOrTenantAccess (see routes/refinement-suggestions.ts's
// own comment — capture exposes nothing a project-members-only caller
// couldn't already see, and never touches the PRD), not this page's own
// assertArtefactTenantWriteAccess gate. No new client-side role check is
// added here: the button lives on the SAME page as Accept/Reject, so it
// inherits the identical structural exclusion those already rely on — a
// CLIENT_SPONSOR still can't reach this page at all to see the button, the
// same reasoning as this file's own header comment above.

interface RefinementSuggestion {
  id: string;
  sourceType: 'annotation' | 'guided_answer' | 'open_question' | 'open_question_fr_update';
  targetFrId: string | null;
  // #2367 Tier 1 — recovered from the source annotation via a read-time
  // JOIN (refinement-suggestions.ts), not stored on this row. Both null for
  // an open_question-sourced suggestion (no source annotation to join to).
  screenId: string | null;
  questionLabel: string | null;
  // #2367 Tier 2 — the friendly name ("Invoice Queue"), derived server-side
  // from the annotation's own prototype version at read time. null when
  // unresolvable (legacy LOW-fidelity version, or no screenId at all) — the
  // display below falls back to the bare screenId in that case.
  screenName: string | null;
  proposedText: string;
  status: 'pending' | 'accepted' | 'rejected';
  reviewedBy: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

interface AcceptResult {
  suggestion: RefinementSuggestion;
  newVersion: number;
  staleWarning?: string;
}

type StatusTab = 'all' | 'pending' | 'accepted' | 'rejected';
const STATUS_TABS: StatusTab[] = ['all', 'pending', 'accepted', 'rejected'];

const SOURCE_LABEL: Record<RefinementSuggestion['sourceType'], string> = {
  annotation: 'Screen annotation',
  guided_answer: 'Guided question',
  open_question: 'Resolved open question',
  // #2456 — a SEPARATE, optional offer alongside the plain "Resolved open
  // question" suggestion above: this resolution's own text referenced this
  // FR, so accepting THIS row updates the FR's body (not §9.2). Distinct
  // label so a founder never confuses the two cards for the same OQ.
  open_question_fr_update: 'Open question — update referenced requirement',
};

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  pending:  { bg: 'rgba(245,158,11,0.12)', color: 'var(--warning)' },
  accepted: { bg: 'rgba(16,185,129,0.12)', color: 'var(--success)' },
  rejected: { bg: 'rgba(120,113,108,0.12)', color: 'var(--text-secondary)' },
};

function Badge({ text, style }: { text: string; style: { bg: string; color: string } }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 9999,
      fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-body)',
      background: style.bg, color: style.color, letterSpacing: '0.02em',
      textTransform: 'uppercase',
    }}>
      {text}
    </span>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// #2497 follow-up (Model A one-Accept) — the two sourceTypes that used to
// route through THIS page's own Accept/Reject before the Open Questions
// page grew a one-Accept flow that folds the same write in directly.
// annotation/guided_answer rows are unaffected — this page remains their
// only accept surface.
function isOpenQuestionSourced(s: RefinementSuggestion): boolean {
  return s.sourceType === 'open_question' || s.sourceType === 'open_question_fr_update';
}

// Maps the PATCH route's distinct failure modes (see routes/refinement-
// suggestions.ts) to founder-legible text. 409/422 are outcomes of the
// claim-first accept path (piece 4b), not generic HTTP noise, so a bare
// "HTTP 409" would leave the founder unable to tell "someone beat you to
// it" apart from "the PRD changed under this suggestion."
function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 409) return 'Already reviewed by someone else — refresh to see the current status.';
    if (e.status === 422) return "Couldn't apply this suggestion: its target requirement text was not found in the current PRD. It may have been edited elsewhere.";
    if (e.status === 403) return "You don't have permission to review suggestions on this project.";
    if (e.status === 404) return 'This suggestion no longer exists.';
    return e.message;
  }
  return e instanceof Error ? e.message : 'Something went wrong.';
}

export default function RefinementSuggestionsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('status') ?? 'all') as StatusTab;

  const [suggestions, setSuggestions] = useState<RefinementSuggestion[]>([]);
  const [loading, setLoading]     = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const [actingId, setActingId]       = useState<string | null>(null);
  const [actionError, setActionError] = useState<Record<string, string>>({});
  const [staleBanner, setStaleBanner] = useState<{ id: string; message: string } | null>(null);

  // #2364 — the manual capture-scan trigger. `capturing` disables the button
  // for the duration of one call (the route is idempotent, but this avoids a
  // double-click firing two overlapping scans); `captureMessage` is a
  // transient founder-legible result ("N generated" / "no new"), replaced by
  // the next click or cleared implicitly on navigation away.
  const [capturing, setCapturing]           = useState(false);
  const [captureMessage, setCaptureMessage] = useState<string | null>(null);
  const [captureError, setCaptureError]     = useState<string | null>(null);

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    const url = activeTab === 'all'
      ? `/projects/${projectId}/refinement-suggestions`
      : `/projects/${projectId}/refinement-suggestions?status=${activeTab}`;
    apiFetch<RefinementSuggestion[]>(url)
      .then(rows => { setSuggestions(rows); setError(null); })
      .catch(e => setError(describeError(e)))
      .finally(() => { setLoading(false); setHasLoaded(true); });
  }, [projectId, activeTab]);

  useEffect(() => { load(); }, [load]);

  // One id per call, always — this is the ONLY function in this page that
  // reaches the PATCH route, and it takes exactly one suggestion id. There
  // is no variant that accepts an array/filter.
  async function act(s: RefinementSuggestion, status: 'accepted' | 'rejected') {
    if (!projectId) return;
    setActingId(s.id);
    setActionError(prev => { const next = { ...prev }; delete next[s.id]; return next; });
    setStaleBanner(null);
    try {
      const result = await apiFetch<AcceptResult | RefinementSuggestion>(
        `/refinement-suggestions/${s.id}`,
        { method: 'PATCH', body: JSON.stringify({ status }) },
      );
      // Accept and reject return differently-shaped bodies (accept:
      // {suggestion,newVersion,staleWarning?}; reject: the bare row) —
      // refetching the list rather than merging either shape into local
      // state avoids needing to reconcile them.
      if (status === 'accepted' && result && 'staleWarning' in result && result.staleWarning) {
        setStaleBanner({ id: s.id, message: result.staleWarning });
      }
      load();
    } catch (e) {
      setActionError(prev => ({ ...prev, [s.id]: describeError(e) }));
    } finally {
      setActingId(null);
    }
  }

  // #2364 — POST the existing capture-scan route, then refresh the list so
  // any newly-captured pending suggestions appear immediately. `{ inserted }`
  // is the route's own, already-shipped response shape
  // (routes/refinement-suggestions.ts's POST handler) — nothing new to add
  // server-side.
  async function generateSuggestions() {
    if (!projectId) return;
    setCapturing(true);
    setCaptureError(null);
    setCaptureMessage(null);
    try {
      const result = await apiFetch<{ inserted: number }>(
        `/projects/${projectId}/refinement-suggestions/capture`,
        { method: 'POST' },
      );
      setCaptureMessage(
        result.inserted > 0
          ? `${result.inserted} new suggestion${result.inserted === 1 ? '' : 's'} generated.`
          : 'No new suggestions — everything answered so far is already captured.',
      );
      load();
    } catch (e) {
      setCaptureError(describeError(e));
    } finally {
      setCapturing(false);
    }
  }

  const btnPrimary: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
    padding: '6px 14px', borderRadius: 'var(--radius-sm)',
    background: 'var(--primary)', color: '#fff', border: 'none', cursor: 'pointer',
  };

  const btnGhost: React.CSSProperties = {
    ...btnPrimary, background: 'transparent', color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', padding: '18px 20px',
  };

  return (
    <main style={{ flex: 1, overflow: 'auto', padding: '32px 40px', maxWidth: 900 }}>
      <div style={{ marginBottom: 24 }}>
        <Link
          to={`/projects/${projectId}`}
          style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}
        >
          ← Dashboard
        </Link>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginTop: 6 }}>
          <div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
              Refinement Suggestions
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
              Accept a suggestion to fold it into the PRD. Reject to dismiss it — nothing changes until you act. Use &ldquo;Regenerate Prototype&rdquo; on the PRD artefact card when you want the preview to reflect the update.
            </p>
          </div>
          {/* #2364 — only offered once the list itself has loaded successfully:
              a GET failure (403 for a caller assertArtefactTenantWriteAccess
              rejects, or any other load error) means this viewer can't see
              the surface the button feeds, so it stays hidden rather than
              rendering a functional action next to an error message. Not a
              new role check — just not offering an action on a page whose
              own data we already know failed to load, the same load/error
              distinction the rest of this page already makes below. */}
          {hasLoaded && !error && (
            <button
              type="button"
              data-testid="generate-suggestions-button"
              onClick={() => void generateSuggestions()}
              disabled={capturing || !projectId}
              style={{
                fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                padding: '6px 14px', borderRadius: 'var(--radius-sm)',
                background: 'transparent', color: 'var(--primary)',
                border: '1px solid var(--primary)', cursor: capturing ? 'default' : 'pointer',
                opacity: capturing ? 0.6 : 1, flexShrink: 0, whiteSpace: 'nowrap',
              }}
            >
              {capturing ? 'Scanning…' : 'Generate suggestions'}
            </button>
          )}
        </div>
        {captureMessage && (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--success)', margin: '8px 0 0' }}>
            {captureMessage}
          </p>
        )}
        {captureError && (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', margin: '8px 0 0' }}>
            {captureError}
          </p>
        )}
      </div>

      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {STATUS_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setSearchParams(tab === 'all' ? {} : { status: tab })}
            style={{
              fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: activeTab === tab ? 600 : 400,
              padding: '7px 14px', background: 'none', border: 'none', cursor: 'pointer',
              color: activeTab === tab ? 'var(--primary)' : 'var(--text-secondary)',
              borderBottom: `2px solid ${activeTab === tab ? 'var(--primary)' : 'transparent'}`,
              marginBottom: -1,
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {loading && !hasLoaded ? (
        <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', fontSize: 14 }}>Loading…</p>
      ) : error ? (
        <p style={{ fontFamily: 'var(--font-body)', color: 'var(--error)', fontSize: 14 }}>{error}</p>
      ) : suggestions.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 20px' }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
            {activeTab === 'all' ? 'No refinement suggestions yet.' : `No ${activeTab} suggestions.`}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {suggestions.map(s => (
            <div key={s.id} style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <Badge text={s.status} style={STATUS_STYLE[s.status] ?? STATUS_STYLE.pending} />
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)' }}>
                      {SOURCE_LABEL[s.sourceType]}
                      {/* #2367 Tier 2 — prefer the friendly name; fall back to the
                          bare screenId (Tier 1) when no name is resolvable (legacy
                          LOW-fidelity version). null for an open_question row. */}
                      {(s.screenName ?? s.screenId) ? ` · ${s.screenName ?? s.screenId}` : ''}
                      {s.targetFrId ? ` · ${s.targetFrId}` : ''} · suggested {formatDate(s.createdAt)}
                    </span>
                  </div>
                  {/* #2367 Tier 1 — the guided question this answers, shown above
                      the bare answer so a PM isn't left guessing what "Nothing is
                      missing" was actually answering. null for a plain annotation
                      or an open_question row. */}
                  {s.questionLabel && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontStyle: 'italic', color: 'var(--text-secondary)', margin: '0 0 4px' }}>
                      {s.questionLabel}
                    </p>
                  )}
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)', margin: 0, lineHeight: 1.5 }}>
                    {s.proposedText}
                  </p>

                  {s.status !== 'pending' && (
                    <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--hover, rgba(120,113,108,0.06))' }}>
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                        {s.status === 'accepted' ? 'Accepted' : 'Rejected'}
                        {s.reviewedByName ? ` by ${s.reviewedByName}` : ''}
                        {s.reviewedAt ? ` on ${formatDate(s.reviewedAt)}` : ''}
                      </p>
                    </div>
                  )}

                  {staleBanner?.id === s.id && (
                    <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(245,158,11,0.1)' }}>
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--warning)', margin: 0 }}>
                        {staleBanner.message}
                      </p>
                    </div>
                  )}

                  {actionError[s.id] && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', marginTop: 8, marginBottom: 0 }}>
                      {actionError[s.id]}
                    </p>
                  )}
                </div>

                {s.status === 'pending' && (
                  isOpenQuestionSourced(s) ? (
                    // #2497 follow-up (Model A one-Accept) — an open-question-
                    // sourced suggestion is no longer independently
                    // actionable HERE: accepting/modifying/deferring/
                    // escalating the question on the Open Questions page now
                    // folds this straight into the PRD in one action. Leaving
                    // Accept/Reject live here was the exact gap (a second,
                    // separate accept surface) this follow-up closes — read-
                    // only pointer instead, not removed from the list
                    // entirely (the row is still useful as a record of what's
                    // outstanding). Screen-annotation and guided-answer rows
                    // are UNRELATED to #2497 and keep their own Accept/Reject
                    // below, unchanged.
                    <div style={{ flexShrink: 0, textAlign: 'right', maxWidth: 220 }}>
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0, fontStyle: 'italic' }}>
                        Accept from the Open Questions page
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button
                        onClick={() => void act(s, 'accepted')}
                        disabled={actingId === s.id}
                        style={{ ...btnPrimary, opacity: actingId === s.id ? 0.6 : 1 }}
                      >
                        {actingId === s.id ? 'Working…' : 'Accept'}
                      </button>
                      <button
                        onClick={() => void act(s, 'rejected')}
                        disabled={actingId === s.id}
                        style={{ ...btnGhost, opacity: actingId === s.id ? 0.6 : 1 }}
                      >
                        Reject
                      </button>
                    </div>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
